export type ManuscriptBlock =
  | { type: 'p'; text: string }
  | { type: 'eq'; tex: string; label: string; aria: string }
  | { type: 'figure'; id: 'orbits'; caption: string }
  | { type: 'table'; caption: string; head: string[]; rows: string[][] }
  | { type: 'quote'; text: string; source: string };

export type ManuscriptSection = { id: string; number: string; heading: string; blocks: ManuscriptBlock[] };

export const manuscriptMeta = {
  series: 'Working Paper · No. 01',
  title: '0/1',
  titleRest: 'On stillness, action and the recursion of quality',
  authors: 'KAYS & KAYS Research Office',
  affiliation: 'KAYS VENTURES FZCO · Dubai Silicon Oasis, Dubai, United Arab Emirates',
  published: 'October 2026',
  version: 'v1.0',
  pages: '14 pages',
  reading: '18 min read',
  identifier: 'KK-WP-2026-01',
  pdf: '/manuscript-0-1.pdf',
  citation:
    'KAYS & KAYS Research Office (2026). 0/1: On stillness, action and the recursion of quality. Working Paper No. 01, KK-WP-2026-01. Dubai: KAYS VENTURES FZCO.',
};

export const manuscriptAbstract =
  'This paper proposes a simple model for how quality accumulates across financial, cultural and human domains. We treat every decision as a choice between 0 — deliberate stillness — and 1 — committed action, and argue that lasting outcomes depend less on the frequency of action than on the stability of the constant that governs it. Borrowing the iterated map z → z² + c, we show informally that a well-chosen constant keeps a system bounded and self-reinforcing, while a poorly chosen one diverges regardless of effort. We outline how the model informs the structure of a family office ecosystem, discuss duration as the dominant multiplier, and close with the limitations of applying a mathematical metaphor to human affairs.';

export const manuscriptKeywords = ['Recursion', 'Quality', 'Stillness', 'Compounding', 'Longevity', 'Family office'];

export const manuscriptSections: ManuscriptSection[] = [
  {
    id: 'introduction',
    number: '1',
    heading: 'Introduction',
    blocks: [
      {
        type: 'p',
        text: 'Most frameworks for building wealth begin with activity: more transactions, more exposure, more reach. The implicit assumption is that output scales with input. In practice, the systems that endure across generations tend to show the opposite pattern. They act rarely, decline often, and hold a small number of commitments for unusually long periods.',
      },
      {
        type: 'p',
        text: 'This paper attempts to describe that pattern precisely enough to be useful. We do not claim a predictive theory. Our aim is narrower: to give a shared vocabulary to the principle that guides the KAYS & KAYS ecosystem — that quality, once chosen as a constant and applied without exception, amplifies itself.',
      },
    ],
  },
  {
    id: 'zero-and-one',
    number: '2',
    heading: 'The binary of 0 and 1',
    blocks: [
      {
        type: 'p',
        text: 'We define 0 as deliberate stillness: the decision not to act, held consciously rather than by default. We define 1 as committed action: a decision executed fully, with resources and attention concentrated behind it. Every meaningful choice can be reduced to a sequence of 0s and 1s.',
      },
      {
        type: 'p',
        text: 'The value of the binary lies in what it excludes. Partial commitment — a 0.5 — is the most common state in organisations and the most expensive. It consumes attention without producing either the clarity of stillness or the force of action.',
      },
      {
        type: 'quote',
        text: 'The discipline is not in choosing 1 more often. It is in making every 0 a decision.',
        source: 'Internal note, 2025',
      },
    ],
  },
  {
    id: 'recursive-model',
    number: '3',
    heading: 'A recursive model of quality',
    blocks: [
      {
        type: 'p',
        text: 'To describe how a constant shapes long-run outcomes, we borrow the quadratic map familiar from complex dynamics. Starting from an initial state, each new state is produced by feeding the previous one back into itself and adding a fixed constant:',
      },
      { type: 'eq', tex: 'z₍ₙ₊₁₎ = z₍ₙ₎² + c', label: '1', aria: 'z sub n plus one equals z sub n squared plus c' },
      {
        type: 'p',
        text: 'Here c is the constant — the standard a system refuses to compromise. The term z² represents recursion: each result becomes the input to the next round, so effects compound rather than add. The sequence z is what emerges over time.',
      },
      {
        type: 'p',
        text: 'The central property of the map is sensitivity to c. For some constants the sequence settles and stays bounded indefinitely; for others it escapes, no matter how small the starting value. Figure 1 shows two such orbits. The lesson we draw is qualitative: effort applied to an unsound constant accelerates divergence, while a sound constant converts the same recursion into stability.',
      },
      {
        type: 'figure',
        id: 'orbits',
        caption:
          'Orbits of the real quadratic map from z₀ = 0. With c = −0.4 (solid) the sequence converges to a stable point; with c = 0.3 (dashed) it escapes after a few iterations. Same recursion, different constant.',
      },
    ],
  },
  {
    id: 'three-domains',
    number: '4',
    heading: 'Application across three domains',
    blocks: [
      {
        type: 'p',
        text: 'The ecosystem applies the same structure in each of its domains. Table 1 summarises how the constant, the recursion and the emergent result are understood in Capital, Culture and Longevity.',
      },
      {
        type: 'table',
        caption: 'The recursive model mapped onto the three domains of the ecosystem.',
        head: ['Domain', 'Constant (c)', 'Recursion (z²)', 'Emergent (z)'],
        rows: [
          ['Capital', 'Off-market access and selective curation', 'Returns reinvested into infrastructure', 'Durable, compounding asset base'],
          ['Culture', 'Brand integrity and editorial standards', 'Reach that attracts aligned partners', 'Authority that does not need to be bought'],
          ['Longevity', 'Cognitive and physical health as baseline', 'Better decisions from a better state', 'Extended horizon for every other domain'],
        ],
      },
      {
        type: 'p',
        text: 'The domains are not independent. Health improves the quality of capital decisions; capital funds cultural assets; cultural reach opens access to new capital. The recursion therefore operates both within and between domains.',
      },
    ],
  },
  {
    id: 'duration',
    number: '5',
    heading: 'Duration as the dominant multiplier',
    blocks: [
      {
        type: 'p',
        text: 'If quality compounds at a modest rate q per period, the accumulated result after t periods is approximated by the familiar growth expression:',
      },
      { type: 'eq', tex: 'Q(t) = Q₀ · (1 + q)ᵗ', label: '2', aria: 'Q of t equals Q zero times one plus q to the power t' },
      {
        type: 'p',
        text: 'Equation (2) makes an obvious but frequently ignored point: beyond a certain horizon, t matters more than q. A system that sustains a moderate rate for decades outperforms one that achieves a high rate briefly. Biological and financial time are therefore treated as the same scarce asset, and protecting duration becomes a strategic priority in its own right.',
      },
    ],
  },
  {
    id: 'discussion',
    number: '6',
    heading: 'Discussion and limitations',
    blocks: [
      {
        type: 'p',
        text: 'The quadratic map is used here as a metaphor, not as an empirical model. Human systems are noisy, constants drift, and outcomes depend on circumstances no framework can anticipate. We make no claim that the model predicts returns or guarantees resilience.',
      },
      {
        type: 'p',
        text: 'Its value is as a decision filter. Asking whether an opportunity strengthens the constant, compounds through recursion, and extends duration has proven a useful test — one that removes most options early and concentrates attention on the few that remain.',
      },
    ],
  },
  {
    id: 'conclusion',
    number: '7',
    heading: 'Conclusion',
    blocks: [
      {
        type: 'p',
        text: 'Quality amplifies quality. Choose one uncompromising constant, let results feed back into the system, and give the process enough time. Between 0 and 1, the rarer and more deliberate choice is usually the one that endures.',
      },
    ],
  },
];

export const manuscriptReferences = [
  'Csikszentmihalyi, M. (1990). Flow: The Psychology of Optimal Experience. New York: Harper & Row.',
  'Kahneman, D. (2011). Thinking, Fast and Slow. New York: Farrar, Straus and Giroux.',
  'Mandelbrot, B. B. (1982). The Fractal Geometry of Nature. San Francisco: W. H. Freeman.',
  'Seneca (2015). Letters on Ethics: To Lucilius (M. Graver & A. A. Long, Trans.). Chicago: University of Chicago Press.',
  'Taleb, N. N. (2012). Antifragile: Things That Gain from Disorder. New York: Random House.',
];
